<div class="min-h-screen flex items-center justify-center bg-[#FFFFFF] p-4">
  <div
    class="flex flex-col md:flex-row w-full max-w-6xl bg-[#FFFFFF] rounded-lg shadow-xl overflow-hidden min-h-[650px]"
  >
    <div class="w-full md:w-1/2 p-8 lg:p-16">
      <h2 class="text-3xl font-semibold text-[#0D3543] mb-8">
        {{ 'auth.registerPage.title' | translate }}
        <span class="text-[#2596BE]">{{ 'auth.common.storeName' | translate }}</span>
      </h2>

      <form
        [formGroup]="registerForm"
        (ngSubmit)="onRegister()"
        class="space-y-6"
        novalidate
      >
        @if (serverError) {
          <p role="alert" class="p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{{ serverError }}</p>
        }

        <div>
          <label class="block text-[#0D3543] mb-2 text-sm">
            {{ 'auth.common.email' | translate }}
          </label>
          <div class="relative flex items-center">
            <sham-i-mail-01-Stroke class="absolute start-3 text-[#6C737F] *:size-5" />
            <input
              type="email"
              formControlName="email"
              [placeholder]="'auth.common.email' | translate"
              autocomplete="email"
              [attr.aria-invalid]="hasControlError('email')"
              [attr.aria-describedby]="hasControlError('email') ? 'register-email-error' : null"
              class="w-full ps-10 pe-4 py-3 border rounded-md focus:ring-2 focus:ring-secondary/20 outline-none text-[#0D3543] placeholder:text-gray-400"
              [class.border-red-400]="hasControlError('email')"
              [class.border-[#BBDEEB]]="!hasControlError('email')"
            />
          </div>
          @if (hasControlError('email')) {
            <p id="register-email-error" role="alert" class="mt-2 text-sm text-red-600">
              {{ getEmailErrorMessage() }}
            </p>
          }
        </div>

        <div>
          <label class="block text-[#0D3543] mb-2 text-sm">
            {{ 'auth.common.phoneNumber' | translate }}
          </label>
          <div class="relative flex items-center">
            <sham-i-phone-03 class="absolute start-3 text-[#6C737F] *:size-5" />
            <input
              type="tel"
              formControlName="phoneNumber"
              [placeholder]="'auth.common.phoneNumber' | translate"
              autocomplete="tel"
              class="w-full ps-10 pe-4 py-3 border rounded-md focus:ring-2 focus:ring-secondary/20 outline-none"
              [class.border-red-400]="hasControlError('phoneNumber')"
              [class.border-[#BBDEEB]]="!hasControlError('phoneNumber')"
            />
          </div>
          @if (hasControlError('phoneNumber')) {
            <p class="mt-2 text-sm text-red-600">
              {{ getPhoneErrorMessage() }}
            </p>
          }
        </div>

        <div class="flex gap-4">
          <div class="flex-1">
            <label class="block text-[#0D3543] mb-2 text-sm">
              {{ 'auth.common.password' | translate }}
            </label>
            <div class="relative flex items-center">
              <sham-i-lock-password-Stroke
                class="absolute start-3 text-[#6C737F] *:size-5"
              />
              <input
                type="password"
                formControlName="password"
                placeholder="****"
                autocomplete="new-password"
                class="w-full ps-10 pe-4 py-3 border rounded-md outline-none"
                [class.border-red-400]="hasControlError('password')"
                [class.border-[#BBDEEB]]="!hasControlError('password')"
              />
            </div>
            @if (hasControlError('password')) {
              <p class="mt-2 text-sm text-red-600">
                {{ getPasswordErrorMessage() }}
              </p>
            }
          </div>

          <div class="flex-1">
            <label class="block text-[#0D3543] mb-2 text-sm">
              {{ 'auth.common.confirmPassword' | translate }}
            </label>
            <div class="relative flex items-center">
              <sham-i-lock-password-Stroke
                class="absolute start-3 text-[#6C737F] *:size-5"
              />
              <input
                type="password"
                formControlName="confirmPassword"
                placeholder="****"
                autocomplete="new-password"
                class="w-full ps-10 pe-4 py-3 border rounded-md outline-none"
                [class.border-red-400]="isConfirmPasswordInvalid()"
                [class.border-[#BBDEEB]]="!isConfirmPasswordInvalid()"
              />
            </div>
            @if (isConfirmPasswordInvalid()) {
              <p class="mt-2 text-sm text-red-600">
                {{ getConfirmPasswordErrorMessage() }}
              </p>
            }
          </div>
        </div>

        <div class="flex items-start gap-3 text-[#0D3543]">
          <input
            type="checkbox"
            id="agree-to-terms"
            formControlName="agreeToTerms"
            class="mt-0.5 size-4 shrink-0 cursor-pointer rounded border border-[#BBDEEB] accent-[#2596BE] focus:ring-2 focus:ring-[#2596BE]/30"
          />
          <label for="agree-to-terms" class="cursor-pointer text-sm leading-5">
            {{ 'auth.registerPage.bySigningUp' | translate }}
            <a routerLink="/terms-and-conditions" class="font-medium">{{
              'auth.common.termsOfService' | translate
            }}</a>
            &
            <a routerLink="/privacy-policy" class="font-medium">{{
              'auth.common.privacyPolicy' | translate
            }}</a>
            .
          </label>
        </div>
        @if (hasControlError('agreeToTerms')) {
          <p class="text-sm text-red-600">
            {{ getTermsErrorMessage() }}
          </p>
        }

        <button
          [disabled]="isLoading"
          type="submit"
          class="w-full bg-[#2596BE] hover:bg-[#2589a5] disabled:bg-gray-300 disabled:cursor-not-allowed text-[#FFFFFF] font-semibold py-3 rounded-xl transition-all flex items-center justify-center gap-2"
        >
          <svg
            *ngIf="isLoading"
            class="animate-spin h-5 w-5 text-white"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              class="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              stroke-width="4"
            ></circle>
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span>
            {{ (isLoading ? 'Loading...' : 'auth.common.register') | translate }}
          </span>
        </button>

        <p class="text-center text-xs text-[#0D3543]">
          {{ 'auth.registerPage.alreadyHaveAccount' | translate }}
          <a routerLink="/auth/login" class="text-[#2187AB] font-medium">
            {{ 'auth.common.login' | translate }}
          </a>
        </p>
      </form>
    </div>

    <div class="w-full md:w-1/2 relative hidden md:block min-h-[650px]">
      <sham-slides-swiper
        class="register-specific-swiper"
        [slides]="registerSlides()"
      >
      </sham-slides-swiper>
    </div>
  </div>
</div>
